import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Plus, X, Sparkles, GraduationCap, Coffee, ClipboardList, CalendarPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useChatWidget } from "@/hooks/useChatWidget";

const HIDDEN_PATHS = ["/admin", "/create-play-date", "/auth", "/reset-password"];

/**
 * Global "+" action button. Opens a short menu of the things a member can
 * contribute. It only routes into existing flows — no submission logic lives
 * here.
 *
 * Variants:
 *  - "floating": desktop bottom-right floating action button.
 *  - "nav":      compact icon button inside the desktop top navigation bar.
 *  - "tab":      centred tab inside the mobile bottom navigation bar (always
 *                visible — the bar itself handles route visibility).
 */
export const FloatingAddButton = ({
  variant = "floating",
}: {
  variant?: "floating" | "nav" | "tab";
} = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen: chatOpen } = useChatWidget();
  const [open, setOpen] = useState(false);

  const path = location.pathname;
  const hidden =
    variant !== "tab" &&
    (HIDDEN_PATHS.includes(path) ||
      path.startsWith("/messages/") ||
      chatOpen);

  if (hidden) return null;

  const go = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  const items = [
    {
      icon: CalendarPlus,
      label: "Organise a meet up",
      hint: "Set a time and place, and invite people",
      onClick: () => navigate("/create-play-date"),
    },
    {
      icon: Sparkles,
      label: "List an activity",
      hint: "A local session, group or event",
      onClick: () =>
        navigate("/admin", { state: { showForm: true, asUser: true, listingType: "community" as const } }),
    },
    {
      icon: GraduationCap,
      label: "List a class",
      hint: "Term-based, one-off or specific dates",
      onClick: () =>
        navigate("/admin", { state: { showForm: true, asUser: true, listingType: "class" as const } }),
    },
    {
      icon: Coffee,
      label: "List a cafe or cafe play",
      hint: "Baby-friendly cafes and cafe plays",
      onClick: () =>
        navigate("/admin", { state: { showForm: true, asUser: true, listingType: "cafe" as const } }),
    },
    {
      icon: ClipboardList,
      label: "Post on the noticeboard",
      hint: "Jobs, services, buy / sell / give, lost & found",

      onClick: () => navigate("/noticeboard?create=1"),
    },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === "nav" ? (
          <button
            type="button"
            aria-label="Add a listing"
            aria-expanded={open}
            className="hidden md:flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          </button>
        ) : variant === "tab" ? (
          <button
            type="button"
            aria-label="Add a listing"
            aria-expanded={open}
            className="min-w-0 flex-1 flex items-center justify-center px-1 py-0"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_7px_16px_-7px_hsl(var(--primary)/0.75)] transition-transform duration-200 active:scale-95">
              {open ? <X className="h-6 w-6" strokeWidth={2.75} /> : <Plus className="h-6 w-6" strokeWidth={2.75} />}
            </span>
          </button>
        ) : (
          <button
            type="button"
            aria-label="Add a listing"
            aria-expanded={open}
            className="hidden md:flex fixed z-40 right-4 bottom-6 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all items-center justify-center"
          >
            {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        side={variant === "nav" ? "bottom" : "top"}
        align={variant === "tab" ? "center" : "end"}
        sideOffset={12}
        className="w-[min(22rem,calc(100vw-2rem))] p-2 z-50"
      >
        <p className="px-2 pt-1 pb-2 text-sm font-semibold text-muted-foreground">
          Add to Balham Babes
        </p>
        <div className="flex flex-col">
          {items.map(({ icon: Icon, label, hint, onClick }) => (
            <button
              key={label}
              type="button"
              onClick={() => go(onClick)}
              className="flex items-start gap-3 rounded-lg px-2 py-3 min-h-11 text-left hover:bg-primary/10 transition-colors"
            >
              <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-[18px] w-[18px]" />
              </span>
              <span className="min-w-0">
                <span className="block text-base font-semibold text-foreground">{label}</span>
                <span className="block text-sm text-muted-foreground">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
